import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { MoveDocModal } from "components/documents/documentsModal/MoveDocModal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { FormikProps } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { FileIcon } from "react-file-utils";
import {
  useGetMessageAttachmentsQuery,
  useMoveChatFilesMutation,
} from "store/apis/chat";
import { useGetGroupDataQuery } from "store/apis/group";
import { MessageResponse } from "stream-chat";
import {
  DefaultStreamChatGenerics,
  useChannelStateContext,
} from "stream-chat-react";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

const FileLabel = ({
  mimeType,
  title,
  documentPath,
}: {
  mimeType: string;
  title: string;
  documentPath: string;
}) => (
  <div className="t-flex t-items-center t-gap-3">
    <FileIcon mimeType={mimeType} version="2" />
    <div>
      <div className="t-body t-text-text-60">{title}</div>
      {documentPath && (
        <div className="t-text-caption t-italic t-text-text-30 t-line-clamp-1">
          Current location: {documentPath}
        </div>
      )}
    </div>
  </div>
);

export const MoveChatDocuments = ({
  isOpen,
  close,
  message,
}: ModalProps & {
  message: MessageResponse<DefaultStreamChatGenerics>;
}) => {
  const {
    data: attachments = [],
    isLoading,
    isSuccess,
    isFetching,
  } = useGetMessageAttachmentsQuery(
    {
      messageId: message.id,
    },
    { skip: !message.id, refetchOnMountOrArgChange: true }
  );
  const moveFileModal = useModal();
  const { channel } = useChannelStateContext();
  const { successToast, alertToast } = useToast();
  const [moveChatFiles, { isLoading: isMovingFile }] =
    useMoveChatFilesMutation();

  let { data } = useGetGroupDataQuery(
    { groupId: (channel?.data?.group_uuid as string) || "" },
    { skip: !channel?.data?.group_uuid }
  );

  const initialValues = attachments?.map(() => false) || [];

  const handleMoveSuccess = () => {
    close();
    moveFileModal.close();
  };

  const handleMoveItems = async (
    selectedIndexes: (string | null)[],
    destinationFolderId: string,
    folderName?: string
  ) => {
    try {
      if (!data) return;
      await moveChatFiles({
        destination_folder_id: destinationFolderId,
        groupId: data?.uuid,
        messageId: message.id,
        attachment_indexes: selectedIndexes
          .filter((index) => index !== null)
          .map((index) => Number(index)),
      }).unwrap();

      handleMoveSuccess();
      successToast({
        message: `File moved${
          folderName ? `" to ${folderName}"` : " successfully"
        }`,
      });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild useCustomOverlay>
        <FormikForm
          initialValues={initialValues}
          enableReinitialize
          onSubmit={moveFileModal.open}
        >
          {({ values, setFieldValue, setValues }) => {
            const booleanValues = values as boolean[];
            const selectedIndexes = booleanValues
              .map((value, index) => (value ? index.toString() : null))
              .filter((index): index is string => index !== null);
            const isSelected = selectedIndexes.length > 0;
            const totalFiles = booleanValues.length;
            const isAllSelected = selectedIndexes.length === totalFiles;

            const currentFolderIds = attachments
              .filter((_, index) => booleanValues[index])
              .map((attachment) => attachment.parent_file_id)
              .join(",");

            return (
              <>
                <Modal.Header>
                  <Modal.Title>Select and Move Documents</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <Async.Root
                    isLoading={isLoading || isFetching}
                    isSuccess={isSuccess}
                    isEmpty={attachments.length === 0}
                  >
                    <Async.Empty>
                      <div className="t-h-40 t-flex t-items-center t-justify-center t-text-text-30 t-text-body-sm">
                        No Attachment found
                      </div>
                    </Async.Empty>
                    <Async.Success>
                      <div className="t-flex t-gap-5 t-flex-col">
                        <Checkbox
                          indeterminate={isSelected && !isAllSelected}
                          checked={isAllSelected}
                          name="selectAll"
                          onChange={(e) => {
                            const newBooleanValues = booleanValues.slice();
                            booleanValues.forEach((_, index) => {
                              newBooleanValues[index] = e.target.checked;
                            });
                            setValues(newBooleanValues);
                          }}
                          label={
                            <div className="t-text-body-sm t-text-text-60">
                              {selectedIndexes.length === 0
                                ? "Select all"
                                : `${selectedIndexes.length} selected`}
                            </div>
                          }
                        />
                        {attachments?.map((file, index) => {
                          const documentPath = String(file.document_path || "");

                          return (
                            <Checkbox
                              key={index}
                              name={index.toString()}
                              checked={booleanValues[index]}
                              onChange={(e) => {
                                const newBooleanValues = booleanValues.slice();
                                newBooleanValues[parseInt(e.target.name)] =
                                  e.target.checked;
                                setValues(newBooleanValues);
                              }}
                              label={
                                <FileLabel
                                  mimeType={file.mime_type || ""}
                                  title={file.title}
                                  documentPath={documentPath}
                                />
                              }
                            />
                          );
                        })}
                        {moveFileModal.isOpen && data && (
                          <MoveDocModal
                            {...moveFileModal}
                            itemIds={selectedIndexes}
                            onMoveSuccess={handleMoveSuccess}
                            currentFolderIds={currentFolderIds}
                            groupId={data.uuid}
                            documentsGroupId={data.documents_group_id}
                            handleMoveItems={(
                              destinationFolderId,
                              folderName
                            ) =>
                              handleMoveItems(
                                selectedIndexes,
                                destinationFolderId,
                                folderName
                              )
                            }
                            isMovingFile={isMovingFile}
                          />
                        )}
                      </div>
                    </Async.Success>
                  </Async.Root>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    disabled={!isSelected}
                    type="submit"
                  >
                    Move selected
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
